<div class="social-layout">
  <div class="social-container">
    <div class="tab-bar">
      <div class="segmented" role="tablist">
        <button
          type="button"
          role="tab"
          [class.active]="activeTab === 'profile'"
          [attr.aria-selected]="activeTab === 'profile'"
          (click)="selectTab('profile')"
          data-testid="tab-profile"
        >
          Wall Profile
        </button>
        <button
          type="button"
          role="tab"
          [class.active]="activeTab === 'posts'"
          [attr.aria-selected]="activeTab === 'posts'"
          (click)="selectTab('posts')"
          data-testid="tab-posts"
        >
          Posts
        </button>
      </div>
      <span class="tab-hint">
        {{
          activeTab === 'profile'
            ? 'How diners see your restaurant on the Fynkle Wall.'
            : 'Share updates with diners nearby and your regulars.'
        }}
      </span>
    </div>

    @if (notLive) {
      <div class="empty-state" data-testid="not-live">
        <div class="empty-title">Your restaurant isn't on Fynkle yet</div>
        <div class="empty-body">
          Your Wall Profile is created when your restaurant is published on Fynkle. You can post
          once it is.
        </div>
      </div>
    }

    <!-- ============================ Wall Profile ========================== -->
    @if (activeTab === 'profile' && profile) {
      @if (profileNeedsPersonalising) {
        <div class="personalise-banner" data-testid="personalise-banner">
          We made your Wall Profile when your restaurant was published on Fynkle. Add a tagline and
          a cover photo so diners know what you're about.
        </div>
      }

      <div class="profile-form" data-testid="profile-form">
        <label class="field">
          <span class="field-label">Handle</span>
          <span class="handle-input">
            <span class="at">&#64;</span>
            <input
              type="text"
              [(ngModel)]="profileForm.username"
              (ngModelChange)="handleError = null"
              maxlength="31"
              autocomplete="off"
              data-testid="handle-input"
            />
          </span>
          @if (handleError || handleProblem()) {
            <span class="field-error" data-testid="handle-error">{{
              handleError || handleProblem()
            }}</span>
          } @else {
            <span class="field-hint">Diners find and tag you with this.</span>
          }
        </label>

        <label class="field">
          <span class="field-label">Tagline</span>
          <textarea
            rows="2"
            [(ngModel)]="profileForm.bio"
            [maxlength]="taglineMax"
            placeholder="Handcrafted ice cream &amp; waffle cones in Downtown San Jose"
            data-testid="tagline-input"
          ></textarea>
          <span class="field-hint">{{ profileForm.bio.length }} / {{ taglineMax }}</span>
        </label>

        <div class="field">
          <span class="field-label">Cover photo</span>
          <div class="cover-actions">
            <label class="btn-ghost sm file-btn">
              {{ uploadingCover ? 'Uploading…' : profileForm.coverImageUrl ? 'Change' : 'Upload' }}
              <input
                type="file"
                accept="image/*"
                hidden
                [disabled]="uploadingCover"
                (change)="onCoverFileSelected($event)"
              />
            </label>
            @if (profileForm.coverImageUrl) {
              <button class="link-btn danger" type="button" (click)="removeCover()">Remove</button>
            }
          </div>
          @if (coverError) {
            <span class="field-error">{{ coverError }}</span>
          }
        </div>

        <div class="profile-note">
          Your name and logo come from Restaurant Profile. Fans are diners who follow you, Moments
          your live posts and Karma the likes they get.
        </div>

        <div class="actions">
          <button
            class="btn-primary"
            [disabled]="!canSaveProfile"
            (click)="saveProfile()"
            data-testid="save-profile"
          >
            {{ savingProfile ? 'Saving…' : 'Save' }}
          </button>
          <button class="btn-ghost" type="button" (click)="resetProfileForm()">Discard</button>
        </div>
      </div>
    }

    <!-- ============================ Posts ================================= -->
    @if (activeTab === 'posts' && !notLive) {
      <!-- Photos can be dropped anywhere on the composer. -->
      <div
        class="composer"
        [class.dragging]="dragging"
        (dragover)="onDragOver($event)"
        (dragleave)="dragging = false"
        (drop)="onDrop($event)"
        data-testid="composer"
      >
        <div class="composer-head">
          @if (profile?.profileImageUrl) {
            <img class="avatar" [src]="profile?.profileImageUrl" alt="" />
          } @else {
            <div class="avatar placeholder">{{ profile?.displayName?.charAt(0) || '·' }}</div>
          }
          <div>
            <div class="composer-name">{{ profile?.displayName }}</div>
            <div class="composer-sub">Posting publicly to the Fynkle Wall</div>
          </div>
        </div>
        <textarea
          class="composer-text"
          rows="4"
          [maxlength]="contentMax"
          [(ngModel)]="draft.content"
          placeholder="New on the menu, an event, a special this weekend…"
          data-testid="composer-text"
        ></textarea>
        @if (dragging) {
          <div class="drop-hint">Drop photos to add them</div>
        }
        @if (draftPhotos.length) {
          <div class="draft-photos" data-testid="draft-photos">
            @for (photo of draftPhotos; track photo.preview) {
              <div class="draft-photo" [class.failed]="photo.error">
                <img [src]="photo.preview" alt="" />
                @if (photo.uploading) {
                  <span class="draft-photo-state">Uploading…</span>
                } @else if (photo.error) {
                  <span class="draft-photo-state error" [title]="photo.error">{{
                    photo.error
                  }}</span>
                }
                <button
                  class="draft-photo-remove"
                  type="button"
                  aria-label="Remove photo"
                  (click)="removePhoto(photo)"
                >
                  ×
                </button>
              </div>
            }
          </div>
        }
        <div class="composer-foot">
          <label
            class="btn-ghost sm file-btn"
            [class.disabled]="!canAddPhotos"
            title="Add up to {{ maxPhotos }} photos, or drop them here"
          >
            📷 Add photos
            <input
              type="file"
              [accept]="photoAccept"
              multiple
              hidden
              [disabled]="!canAddPhotos"
              (change)="addPhotos($event)"
              data-testid="photo-input"
            />
          </label>
          <div class="chips" role="radiogroup" aria-label="Post type" data-testid="type-chips">
            @if (draftPhotos.length) {
              <!-- A post with photos is always a photo post. -->
              <span class="chip active" title="Posts with photos are photo posts">{{
                typeLabels['photo']
              }}</span>
            } @else {
              @for (type of postTypes; track type) {
                <button
                  type="button"
                  role="radio"
                  class="chip"
                  [class.active]="draft.type === type"
                  [attr.aria-checked]="draft.type === type"
                  (click)="draft.type = type"
                >
                  {{ typeLabels[type] }}
                </button>
              }
            }
          </div>
          @if (showCounter) {
            <span class="counter" data-testid="counter"
              >{{ draft.content.length }} / {{ contentMax }}</span
            >
          }
          <!-- The wrapper carries the tooltip: a disabled button gets no hover. -->
          <span class="publish-wrap" [title]="publishHint" data-testid="publish-hint">
            <button
              class="btn-primary"
              [disabled]="!canPublish"
              (click)="publish()"
              data-testid="publish"
            >
              {{ publishing ? 'Posting…' : 'Post' }}
            </button>
          </span>
        </div>
      </div>

      @if (posts.length) {
        <div class="chips post-filters" data-testid="post-filters">
          @for (filter of postFilters; track filter.key) {
            <button
              type="button"
              class="chip"
              [class.active]="postFilter === filter.key"
              (click)="postFilter = filter.key"
            >
              {{ filter.label }} <span class="chip-count">{{ filterCount(filter.key) }}</span>
            </button>
          }
        </div>
      }

      @for (post of visiblePosts; track trackPost($index, post)) {
        <div
          class="post-card"
          [class.selected]="selectedPostId === post.postId"
          (click)="selectPost(post)"
          data-testid="post-card"
        >
          <div class="post-head">
            <span class="post-type">{{ typeLabels[post.type] || post.type }}</span>
            <span class="post-date" [title]="formatDateTime(post.created)">
              {{ formatRelative(post.created) }}
              @if (post.editedAt) {
                · edited
              }
            </span>
            <span
              class="badge"
              [ngClass]="'tone-' + badge(post).tone"
              data-testid="moderation-badge"
            >
              {{ badge(post).label }}
            </span>
            <button
              type="button"
              class="more-btn"
              aria-label="Post actions"
              [matMenuTriggerFor]="postMenu"
              (click)="$event.stopPropagation()"
              data-testid="post-menu"
            >
              ⋯
            </button>
            <mat-menu #postMenu="matMenu" xPosition="before">
              <button mat-menu-item (click)="startEdit(post)">Edit</button>
              <button mat-menu-item class="danger" (click)="confirmDelete(post)">Delete</button>
            </mat-menu>
          </div>

          @if (editingId === post.postId) {
            <textarea
              class="composer-text"
              rows="3"
              [maxlength]="contentMax"
              [(ngModel)]="editContent"
              data-testid="edit-text"
            ></textarea>
            @if (editPhotos.length) {
              <div class="draft-photos" data-testid="edit-photos">
                @for (photo of editPhotos; track photo.preview) {
                  <div class="draft-photo" [class.failed]="photo.error">
                    <img [src]="photo.preview" alt="" />
                    @if (photo.uploading) {
                      <span class="draft-photo-state">Uploading…</span>
                    } @else if (photo.error) {
                      <span class="draft-photo-state error" [title]="photo.error">{{
                        photo.error
                      }}</span>
                    }
                    <button
                      class="draft-photo-remove"
                      type="button"
                      aria-label="Remove photo"
                      (click)="removePhoto(photo, 'edit')"
                    >
                      ×
                    </button>
                  </div>
                }
              </div>
            }
            <div class="edit-foot">
              <label
                class="btn-ghost sm file-btn"
                [class.disabled]="!canAddEditPhotos"
                title="Add up to {{ maxPhotos }} photos"
              >
                📷 Add photos
                <input
                  type="file"
                  [accept]="photoAccept"
                  multiple
                  hidden
                  [disabled]="!canAddEditPhotos"
                  (change)="addPhotos($event, 'edit')"
                  data-testid="edit-photo-input"
                />
              </label>
              <span class="edit-note">New text and photos are checked again.</span>
              <div class="actions">
                <button class="btn-ghost sm" (click)="cancelEdit()">Cancel</button>
                <button
                  class="btn-primary sm"
                  [disabled]="!canSaveEdit"
                  (click)="saveEdit(post)"
                  data-testid="save-edit"
                >
                  {{ saving ? 'Saving…' : 'Save' }}
                </button>
              </div>
            </div>
          } @else {
            <p class="post-text">{{ post.content }}</p>
            @if (post.mediaUrls.length) {
              <div class="photos">
                @for (url of post.mediaUrls; track url) {
                  <button
                    type="button"
                    class="photo-btn"
                    aria-label="View photo"
                    (click)="openPhoto(url, $event)"
                  >
                    <img class="photo" [src]="url" alt="" />
                  </button>
                }
              </div>
            }
            @if (post.moderationStatus === 'removed' && post.moderationReason) {
              <div class="moderation-note">Removed: {{ post.moderationReason }}</div>
            }
            <div class="stats">
              ♥ {{ post.likesCount }} ·
              <button
                type="button"
                class="comments-btn"
                [class.open]="threads[post.postId]?.open"
                [attr.aria-expanded]="!!threads[post.postId]?.open"
                (click)="toggleComments(post, $event)"
                data-testid="comments-toggle"
              >
                💬 {{ post.commentsCount }}
                {{ post.commentsCount === 1 ? 'comment' : 'comments' }}
              </button>
              · ✨ {{ post.inspiredCount }}
            </div>

            <!-- Comments (FNF-1697): what diners say, read-only. -->
            @if (threads[post.postId]; as thread) {
              @if (thread.open) {
                <div class="comments" (click)="$event.stopPropagation()" data-testid="comments">
                  @if (thread.loading) {
                    <div class="comments-note">Loading comments…</div>
                  } @else if (thread.error) {
                    <div class="comments-note">
                      Could not load comments.
                      <button type="button" class="link-btn" (click)="loadComments(post)">
                        Try again
                      </button>
                    </div>
                  } @else {
                    @for (comment of thread.comments; track comment.interactionId) {
                      <ng-container
                        *ngTemplateOutlet="commentRow; context: { $implicit: comment, post: post }"
                      ></ng-container>
                      @if (comment.replyCount > 0) {
                        <div class="replies">
                          @if (replies[comment.interactionId]; as replyThread) {
                            @if (replyThread.open) {
                              @for (reply of replyThread.items; track reply.interactionId) {
                                <ng-container
                                  *ngTemplateOutlet="
                                    commentRow;
                                    context: { $implicit: reply, post: post }
                                  "
                                ></ng-container>
                              }
                            }
                            @if (replyThread.loading) {
                              <div class="comments-note">Loading replies…</div>
                            } @else if (replyThread.error) {
                              <button
                                type="button"
                                class="link-btn"
                                (click)="loadReplies(post, comment)"
                              >
                                Could not load replies. Try again
                              </button>
                            } @else if (replyThread.open && replyThread.hasMore) {
                              <button
                                type="button"
                                class="link-btn"
                                (click)="loadReplies(post, comment)"
                              >
                                Load more replies
                              </button>
                            }
                          }
                          <button
                            type="button"
                            class="link-btn"
                            (click)="toggleReplies(post, comment)"
                            data-testid="replies-toggle"
                          >
                            {{
                              replies[comment.interactionId]?.open
                                ? 'Hide replies'
                                : 'View ' +
                                  comment.replyCount +
                                  (comment.replyCount === 1 ? ' reply' : ' replies')
                            }}
                          </button>
                        </div>
                      }
                    } @empty {
                      <div class="comments-note">No comments yet.</div>
                    }
                    <!-- A new top-level comment, as the restaurant (FNF-1698). -->
                    <div class="comment-new">
                      <input
                        type="text"
                        class="comment-input"
                        [maxlength]="commentMax"
                        [(ngModel)]="newComment[post.postId]"
                        [placeholder]="'Comment as ' + profile?.displayName + '…'"
                        (keydown.enter)="sendComment(post)"
                        data-testid="new-comment"
                      />
                      <button
                        class="btn-primary sm"
                        type="button"
                        [disabled]="!(newComment[post.postId] || '').trim() || sendingComment"
                        (click)="sendComment(post)"
                        data-testid="send-comment"
                      >
                        Send
                      </button>
                    </div>
                  }
                </div>
              }
            }
          }
        </div>
      } @empty {
        @if (!postsLoading && profile) {
          @if (posts.length) {
            <div class="empty-state">
              <div class="empty-body">No posts here.</div>
            </div>
          } @else {
            <div class="empty-state">
              <div class="empty-title">No posts yet</div>
              <div class="empty-body">
                Your first post reaches diners near your restaurant and the people who order from
                you most.
              </div>
            </div>
          }
        }
      }

      @if (postsCursor) {
        <button class="btn-ghost load-more" [disabled]="postsLoading" (click)="loadPosts()">
          {{ postsLoading ? 'Loading…' : 'Load more' }}
        </button>
      }
    }
  </div>

  <!-- ============================ Previews ============================== -->
  <!-- What diners see on the Fynkle Wall, with the forms' unsaved values. -->
  @if (profile) {
    <aside class="social-aside" data-testid="previews">
      @if (activeTab === 'profile') {
        <div class="preview-label">Wall Profile preview</div>
        <div class="profile-card" data-testid="profile-card">
          <div
            class="profile-cover"
            [style.background-image]="
              profileForm.coverImageUrl ? 'url(' + profileForm.coverImageUrl + ')' : null
            "
          ></div>
          <div class="profile-head">
            @if (profile.profileImageUrl) {
              <img class="profile-logo" [src]="profile.profileImageUrl" alt="" />
            } @else {
              <div class="profile-logo placeholder">{{ profile.displayName.charAt(0) }}</div>
            }
            <div>
              <div class="profile-name">{{ profile.displayName }}</div>
              <div class="profile-handle">&#64;{{ profileForm.username || profile.username }}</div>
            </div>
          </div>
          @if (profile.isHidden) {
            <span class="badge tone-down hidden-badge">Hidden from diners</span>
          }
          @if (profileForm.bio.trim()) {
            <p class="profile-bio">“{{ profileForm.bio.trim() }}”</p>
          }
          <div class="profile-stats">
            <span
              ><strong>{{ profile.followersCount }}</strong> Fans</span
            >
            <span
              ><strong>{{ profile.moments }}</strong> Moments</span
            >
            <span
              ><strong>{{ profile.karma }}</strong> Karma</span
            >
          </div>
        </div>
      } @else {
        <div class="preview-label">
          Post preview
          @if (previewPost; as preview) {
            <span class="preview-source">· {{ preview.source }}</span>
          }
        </div>
        @if (previewPost; as preview) {
          <div class="wall-post" data-testid="post-preview">
            <div class="wall-post-head">
              @if (profile.profileImageUrl) {
                <img class="avatar" [src]="profile.profileImageUrl" alt="" />
              } @else {
                <div class="avatar placeholder">{{ profile.displayName.charAt(0) }}</div>
              }
              <div>
                <div class="composer-name">
                  {{ profile.displayName }}
                  <span class="restaurant-badge">Restaurant</span>
                </div>
                <div class="composer-sub">{{ preview.when }}</div>
              </div>
            </div>
            @if (preview.content) {
              <p class="post-text">{{ preview.content }}</p>
            }
            @if (preview.photos.length) {
              <div class="wall-photos" [class.single]="preview.photos.length === 1">
                @for (url of preview.photos; track url) {
                  <img [src]="url" alt="" />
                }
              </div>
            }
            <div class="stats">
              ♥ {{ preview.likes }} · 💬 {{ preview.comments }} · ✨ {{ preview.inspired }}
            </div>
          </div>
        } @else {
          <!-- A ghost card until there is a draft or a picked post. -->
          <div class="wall-post ghost" data-testid="post-preview-empty">
            <div class="wall-post-head">
              @if (profile.profileImageUrl) {
                <img class="avatar" [src]="profile.profileImageUrl" alt="" />
              } @else {
                <div class="avatar placeholder">{{ profile.displayName.charAt(0) }}</div>
              }
              <div>
                <div class="composer-name">
                  {{ profile.displayName }}
                  <span class="restaurant-badge">Restaurant</span>
                </div>
                <div class="composer-sub">Just now</div>
              </div>
            </div>
            <div class="ghost-line"></div>
            <div class="ghost-line short"></div>
            <div class="ghost-photo"></div>
            <div class="ghost-note">
              Start writing, or pick a post on the left, to see it as diners do.
            </div>
          </div>
        }

        <!-- Who the post comes from, without the full profile card. -->
        <div class="mini-profile" data-testid="mini-profile">
          @if (profile.profileImageUrl) {
            <img class="avatar sm" [src]="profile.profileImageUrl" alt="" />
          } @else {
            <div class="avatar sm placeholder">{{ profile.displayName.charAt(0) }}</div>
          }
          <div class="mini-profile-text">
            <div class="composer-name">{{ profile.displayName }}</div>
            <div class="composer-sub">&#64;{{ profile.username }}</div>
          </div>
          <button type="button" class="link-btn" (click)="selectTab('profile')">
            Edit profile
          </button>
        </div>
      }
    </aside>
  }
</div>

@if (lightboxUrl) {
  <div class="lightbox" (click)="lightboxUrl = null" data-testid="lightbox">
    <img [src]="lightboxUrl" alt="" (click)="$event.stopPropagation()" />
    <button type="button" class="lightbox-close" aria-label="Close" (click)="lightboxUrl = null">
      ×
    </button>
  </div>
}

<!-- One comment or reply. The restaurant's own show its name and badge. -->
<ng-template #commentRow let-comment let-post="post">
  <div class="comment" [class.reply]="comment.parentCommentId" data-testid="comment">
    @if (comment.isPostAuthor && profile) {
      @if (profile.profileImageUrl) {
        <img class="avatar sm" [src]="profile.profileImageUrl" alt="" />
      } @else {
        <div class="avatar sm placeholder">{{ profile.displayName.charAt(0) }}</div>
      }
    } @else if (comment.author.profileImageUrl) {
      <img class="avatar sm" [src]="comment.author.profileImageUrl" alt="" />
    } @else {
      <div class="avatar sm placeholder">{{ authorInitial(comment.author) }}</div>
    }
    <div class="comment-body">
      <div class="comment-head">
        <span class="comment-name">
          {{ comment.isPostAuthor && profile ? profile.displayName : authorName(comment.author) }}
        </span>
        @if (comment.isPostAuthor) {
          <span class="restaurant-badge">Restaurant</span>
        }
        <span class="post-date" [title]="formatDateTime(comment.created)">{{
          formatRelative(comment.created)
        }}</span>
      </div>
      <p class="comment-text">
        @if (comment.replyTo) {
          <span class="mention">&#64;{{ authorName(comment.replyTo).replace('@', '') }}</span>
        }
        {{ comment.content }}
      </p>
      <div class="comment-actions">
        @if (comment.likesCount) {
          <span class="comment-likes">♥ {{ comment.likesCount }}</span>
        }
        <button
          type="button"
          class="link-btn"
          (click)="startReply(comment)"
          data-testid="reply-btn"
        >
          Reply
        </button>
        @if (comment.isPostAuthor) {
          <!-- A comment with replies stays, so they aren't left without it. -->
          <button
            type="button"
            class="link-btn danger"
            [disabled]="!comment.parentCommentId && comment.replyCount > 0"
            [title]="
              !comment.parentCommentId && comment.replyCount > 0
                ? 'Comments with replies can\'t be deleted'
                : ''
            "
            (click)="confirmDeleteComment(post, comment)"
            data-testid="delete-comment"
          >
            Delete
          </button>
        }
      </div>
    </div>
  </div>
  @if (replyingTo?.interactionId === comment.interactionId) {
    <div class="reply-box" [class.nested]="comment.parentCommentId" data-testid="reply-box">
      <textarea
        class="composer-text"
        rows="2"
        [maxlength]="commentMax"
        [(ngModel)]="replyText"
        [placeholder]="'Reply to ' + authorName(comment.author) + ' as ' + profile?.displayName"
        data-testid="reply-text"
      ></textarea>
      <div class="actions">
        <button class="btn-ghost sm" type="button" (click)="cancelReply()">Cancel</button>
        <button
          class="btn-primary sm"
          type="button"
          [disabled]="!canSendReply"
          (click)="sendReply(post)"
          data-testid="send-reply"
        >
          {{ sendingReply ? 'Sending…' : 'Reply' }}
        </button>
      </div>
    </div>
  }
</ng-template>
